// src/services/DependencyEngine.ts
// Manages task dependencies. What blocks what.

import { supabase } from '@/lib/supabase';
import { TaskDependency } from '@/types/Journey.types';

export class DependencyEngine {
  private userId: string;

  constructor(userId: string) {
    this.userId = userId;
  }

  async addDependency(taskId: string, dependsOn: string): Promise<TaskDependency | null> {
    try {
      const { data, error } = await supabase
        .from('dependencies')
        .insert({ task_id: taskId, depends_on: dependsOn })
        .select()
        .single();

      if (error) {
        console.error('DependencyEngine.addDependency error:', error);
        return null;
      }

      return this.transform(data);
    } catch (err) {
      return null;
    }
  }

  async getDependencies(taskId: string): Promise<TaskDependency[]> {
    try {
      const { data, error } = await supabase
        .from('dependencies')
        .select('*')
        .eq('task_id', taskId);

      if (error) return [];
      return (data ?? []).map(this.transform);
    } catch {
      return [];
    }
  }

  async isTaskBlocked(taskId: string): Promise<boolean> {
    try {
      const deps = await this.getDependencies(taskId);
      if (deps.length === 0) return false;

      const dependsOnIds = deps.map(d => d.dependsOn);

      const { data, error } = await supabase
        .from('tasks')
        .select('id, status')
        .in('id', dependsOnIds);

      if (error || !data) return false;

      return data.some((t: any) => t.status !== 'completed');
    } catch {
      return false;
    }
  }

  async getDependencyGraph(): Promise<{
    nodes: Array<{ id: string; label: string; status: string }>;
    edges: Array<{ from: string; to: string }>;
  }> {
    try {
      const { data: tasks, error: tasksError } = await supabase
        .from('tasks')
        .select('id, title, status')
        .eq('user_id', this.userId);

      if (tasksError || !tasks) return { nodes: [], edges: [] };

      const taskIds = tasks.map((t: any) => t.id);
      if (taskIds.length === 0) return { nodes: tasks.map((t: any) => ({ id: t.id, label: t.title, status: t.status })), edges: [] };

      const { data: deps, error: depsError } = await supabase
        .from('dependencies')
        .select('task_id, depends_on')
        .in('task_id', taskIds);

      if (depsError) return { nodes: [], edges: [] };

      return {
        nodes: tasks.map((t: any) => ({ id: t.id, label: t.title, status: t.status })),
        edges: (deps ?? []).map((d: any) => ({ from: d.task_id, to: d.depends_on })),
      };
    } catch {
      return { nodes: [], edges: [] };
    }
  }

  private transform(row: any): TaskDependency {
    return {
      id: row.id,
      taskId: row.task_id,
      dependsOn: row.depends_on,
      createdAt: row.created_at,
    };
  }
}
